import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';
import { getCurrentCorretor, signOutCorretor, onCorretorAuthChange } from '../services/corretorAuthService';
import { listPropertiesByCorretor, formatPrice, buildPropertySlug, getFilterOptions } from '../services/propertyService';
import { listImoveisImobiliaria, listVinculosDoCorretor, listImoveisVinculadosAprovados, solicitarAcesso } from '../services/imovelCorretoresService';
import { MF_BASE } from '../basePath';

const isVideo = (u) => /\.(mp4|webm|mov|m4v)(\?|$)/i.test(u || '');
const getCover = (imagens) => {
  if (!Array.isArray(imagens) || imagens.length === 0) return null;
  return imagens.find(u => !isVideo(u)) || imagens[0];
};

const THEME_KEY = 'mf_corretor_theme';
const darkTheme = {
  mode: 'dark',
  bg: '#0F1117', surface: '#181B23', surfaceAlt: '#1F2330',
  ink: '#F1ECE2', inkSoft: '#C9C4B8', muted: '#8A8F9A',
  line: '#262A36', gold: '#C9A06A', goldSoft: 'rgba(201,160,106,.14)',
  ok: '#5BB089', okSoftBg: 'rgba(91,176,137,.10)',
  reject: '#9A3B3B', rejectBg: 'rgba(154,59,59,.1)',
  shadow: 'rgba(0,0,0,.4)',
};
const lightTheme = {
  mode: 'light',
  bg: '#FAFAF7', surface: '#FFFFFF', surfaceAlt: '#F5F3EE',
  ink: '#1A1F2B', inkSoft: '#3D434F', muted: '#6B7280',
  line: '#E8E5DE', gold: '#B8935A', goldSoft: '#F2EBDD',
  ok: '#2F7D5B', okSoftBg: 'rgba(47,125,91,.06)',
  reject: '#9A3B3B', rejectBg: 'rgba(154,59,59,.06)',
  shadow: 'rgba(0,0,0,.06)',
};

const Shell = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  min-height: 100vh;
  background: ${p => p.theme.bg};
  color: ${p => p.theme.ink};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  transition: background .25s, color .25s;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Sidebar = styled.aside`
  background: ${p => p.theme.surface};
  border-right: 1px solid ${p => p.theme.line};
  padding: 32px 20px;
  display: flex; flex-direction: column; gap: 4px;
  @media (max-width: 900px) {
    border-right: none; border-bottom: 1px solid ${p => p.theme.line};
    flex-direction: row; flex-wrap: wrap; padding: 14px 20px; gap: 6px; align-items: center;
  }
`;

const Brand = styled.div`
  padding: 0 8px 24px;
  border-bottom: 1px solid ${p => p.theme.line};
  margin-bottom: 20px;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  img { height: 56px; width: auto; max-width: 180px; object-fit: contain; }
  .tagline {
    font-size: 10px; letter-spacing: 3px; color: ${p => p.theme.gold};
    text-transform: uppercase; font-weight: 500;
  }
  @media (max-width: 900px) {
    border-bottom: none; padding: 0 8px 0 0; margin: 0 auto 0 0;
    flex-direction: row; gap: 12px;
    img { height: 36px; }
    .tagline { display: none; }
  }
`;

const NavGroup = styled.div`
  font-size: 10px; letter-spacing: 2.5px; color: ${p => p.theme.muted};
  text-transform: uppercase; padding: 16px 8px 8px; font-weight: 500;
  @media (max-width: 900px) { display: none; }
`;

const NavItem = styled.button`
  display: flex; align-items: center; gap: 12px; width: 100%; border: none;
  background: ${p => p.$active ? p.theme.goldSoft : 'transparent'};
  color: ${p => p.$active ? p.theme.ink : p.theme.inkSoft};
  padding: 11px 14px; border-radius: 8px; font-size: 14px;
  font-weight: ${p => p.$active ? 600 : 400};
  cursor: pointer; text-align: left; letter-spacing: 0.2px; transition: background .15s;
  font-family: inherit;
  &:hover { background: ${p => p.$active ? p.theme.goldSoft : p.theme.surfaceAlt}; }
`;

const Main = styled.main`
  display: flex; flex-direction: column;
`;

const Topbar = styled.header`
  display: flex; align-items: center; justify-content: space-between;
  padding: 22px 40px; border-bottom: 1px solid ${p => p.theme.line};
  background: ${p => p.theme.surface}; flex-wrap: wrap; gap: 12px;
  @media (max-width: 600px) { padding: 14px 20px; }
`;

const Crumb = styled.div`
  font-size: 14px; color: ${p => p.theme.muted};
  span { color: ${p => p.theme.ink}; font-weight: 500; }
`;

const TopActions = styled.div`
  display: flex; gap: 10px; align-items: center;
`;

const ThemeToggle = styled.button`
  background: none; border: 1px solid ${p => p.theme.line}; color: ${p => p.theme.muted};
  width: 36px; height: 36px; border-radius: 8px; cursor: pointer; font-size: 16px;
  &:hover { border-color: ${p => p.theme.gold}; color: ${p => p.theme.gold}; }
`;

const TopBtn = styled.button`
  padding: 10px 20px; border-radius: 8px; font-size: 12px; font-weight: 600;
  letter-spacing: 1px; text-transform: uppercase; cursor: pointer; font-family: inherit;
  border: none; background: ${p => p.theme.gold}; color: #fff;
  transition: opacity .15s;
  &:hover { opacity: .85; }
`;

const Content = styled.div`
  padding: 40px 48px; width: 100%;
  @media (max-width: 1100px) { padding: 32px 28px; }
  @media (max-width: 600px) { padding: 24px 16px; }
`;

const PageHead = styled.div`
  display: flex; flex-wrap: wrap; align-items: end; justify-content: space-between;
  gap: 16px; margin-bottom: 24px;
  h1 { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 32px; font-weight: 500; margin: 0; }
`;

const Stats = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px; margin-bottom: 32px;
`;

const StatCard = styled.div`
  padding: 20px; background: ${p => p.theme.surface}; border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  .label { font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: ${p => p.theme.muted}; margin-bottom: 4px; }
  .value { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 28px; font-weight: 500; }
  .meta { font-size: 11px; color: ${p => p.theme.muted}; margin-top: 2px; }
`;

const Table = styled.div`
  background: ${p => p.theme.surface}; border: 1px solid ${p => p.theme.line};
  border-radius: 12px; overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 72px 1fr auto;
  align-items: center; gap: 16px;
  padding: 14px 18px; border-top: 1px solid ${p => p.theme.line};
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  transition: background .15s;
  &:first-of-type { border-top: none; }
  &:hover { background: ${p => p.theme.surfaceAlt}; }
  @media (max-width: 640px) { grid-template-columns: 56px 1fr; .actions { grid-column: 1 / -1; } }
`;

const Thumb = styled.div`
  width: 64px; height: 48px; border-radius: 8px;
  background: ${p => p.$src ? `url(${p.$src}) center/cover` : p.theme.surfaceAlt};
  border: 1px solid ${p => p.theme.line};
  @media (max-width: 640px) { width: 56px; height: 42px; }
`;

const CellInfo = styled.div`
  min-width: 0;
  .title { font-size: 14px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .loc { font-size: 12px; color: ${p => p.theme.muted}; margin-top: 2px; }
  .price { font-size: 13px; color: ${p => p.theme.gold}; font-weight: 500; margin-top: 4px; }
`;

const CellActions = styled.div`
  display: flex; flex-direction: column; align-items: flex-end; gap: 6px;
`;

const Badge = styled.span`
  display: inline-block; padding: 4px 10px; border-radius: 999px;
  font-size: 10px; font-weight: 600; letter-spacing: .8px; text-transform: uppercase;
  background: ${p =>
    p.$s === 'aprovado' ? p.theme.okSoftBg :
    p.$s === 'rejeitado' ? p.theme.rejectBg :
    p.theme.goldSoft};
  color: ${p =>
    p.$s === 'aprovado' ? p.theme.ok :
    p.$s === 'rejeitado' ? p.theme.reject :
    p.theme.gold};
`;

const SmallBtn = styled.button`
  background: ${p => p.$gold ? p.theme.goldSoft : 'transparent'};
  border: 1px solid ${p => p.$gold ? p.theme.gold : p.theme.line};
  color: ${p => p.$gold ? p.theme.gold : p.theme.inkSoft};
  padding: 5px 12px; border-radius: 6px;
  font-size: 10px; letter-spacing: 1px; text-transform: uppercase; font-weight: 600;
  cursor: pointer; font-family: inherit; transition: background .15s;
  &:hover { background: ${p => p.$gold ? 'rgba(201,160,106,.22)' : p.theme.surfaceAlt}; }
  &:disabled { opacity: .45; cursor: not-allowed; }
`;

const RejectMsg = styled.div`
  grid-column: 1 / -1;
  font-size: 12px; color: ${p => p.theme.reject};
  background: ${p => p.theme.rejectBg};
  border: 1px solid rgba(154,59,59,.2);
  border-radius: 6px; padding: 8px 12px; margin-top: 4px;
`;

const FBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: ${p => p.theme.surface};
  border: 1px solid ${p => p.theme.line};
  border-radius: 10px;
  margin-bottom: 20px;
`;

const FSearch = styled.input`
  width: 200px;
  flex-shrink: 1;
  min-width: 120px;
  padding: 8px 10px 8px 32px;
  background: ${p => p.theme.bg} url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='11' cy='11' r='7' stroke='%238A8F9A' stroke-width='2'/%3E%3Cpath d='M16 16l4.5 4.5' stroke='%238A8F9A' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat 10px center;
  border: 1px solid ${p => p.theme.line};
  border-radius: 6px;
  color: ${p => p.theme.ink};
  font-size: 13px;
  font-family: inherit;
  outline: none;
  transition: border-color .15s;
  &:focus { border-color: ${p => p.theme.gold}; }
  &::placeholder { color: ${p => p.theme.muted}; }
`;

const FToggleGroup = styled.div`
  display: flex; border: 1px solid ${p => p.theme.line}; border-radius: 6px; overflow: hidden;
`;
const FToggle = styled.button`
  padding: 8px 14px; border: none; font-size: 12px; font-weight: 600; font-family: inherit;
  cursor: pointer; transition: background .15s, color .15s; letter-spacing: .3px;
  background: ${p => p.$active ? p.theme.gold : 'transparent'};
  color: ${p => p.$active ? '#fff' : p.theme.muted};
  &:hover { color: ${p => p.$active ? '#fff' : p.theme.ink}; }
`;

const FSelect = styled.select`
  padding: 8px 26px 8px 10px;
  background: ${p => p.theme.bg};
  border: 1px solid ${p => p.theme.line};
  border-radius: 6px;
  color: ${p => p.theme.ink};
  font-size: 13px;
  font-family: inherit;
  outline: none;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%238A8F9A' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  transition: border-color .15s;
  &:focus { border-color: ${p => p.theme.gold}; }
`;

const FCount = styled.span`
  font-size: 13px;
  color: ${p => p.theme.muted};
  margin-left: auto;
  white-space: nowrap;
`;

const QUARTOS_OPTS = [
  { label: 'Quartos', value: '' },
  { label: '1+', value: 1 }, { label: '2+', value: 2 },
  { label: '3+', value: 3 }, { label: '4+', value: 4 },
];

const FAIXAS_VENDA = [500000,1000000,1500000,2000000,3000000,5000000,7000000,10000000,15000000,20000000];
const FAIXAS_ALUGUEL = [1000,2000,3000,5000,7000,10000,15000,20000,30000,50000];

const Empty = styled.div`
  padding: 80px 20px; text-align: center; color: ${p => p.theme.muted}; font-size: 14px;
  border: 1px dashed ${p => p.theme.line}; border-radius: 12px;
  background: ${p => p.theme.surface};
`;

export default function CorretorPanelPage() {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', corretor: null });
  const [properties, setProperties] = useState([]);
  const [propsLoading, setPropsLoading] = useState(true);
  const [nav, setNav] = useState('meus');
  const [imobImoveis, setImobImoveis] = useState([]);
  const [vinculos, setVinculos] = useState([]);
  const [imobLoading, setImobLoading] = useState(true);
  const [requesting, setRequesting] = useState(null);
  const [filters, setFilters] = useState({ busca: '', tipo_negocio: '', cidade: '', tipo: '', quartos: '', preco_min: '', preco_max: '' });
  const [filterOptions, setFilterOptions] = useState({ cidades: [], tipos: [] });
  const [mode, setMode] = useState(() => localStorage.getItem(THEME_KEY) || 'dark');
  const theme = mode === 'dark' ? darkTheme : lightTheme;

  const toggleMode = () => {
    const next = mode === 'dark' ? 'light' : 'dark';
    setMode(next);
    localStorage.setItem(THEME_KEY, next);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const res = await getCurrentCorretor();
      if (cancelled) return;
      if (!res) { navigate(`${MF_BASE}/corretor/login`, { replace: true }); return; }
      setState({ status: 'ok', corretor: res.corretor });
      getFilterOptions().then(setFilterOptions);
      const [list, vinculados, imob, vinc] = await Promise.all([
        listPropertiesByCorretor(res.corretor.id),
        listImoveisVinculadosAprovados(res.corretor.id),
        listImoveisImobiliaria(),
        listVinculosDoCorretor(res.corretor.id),
      ]);
      if (cancelled) return;
      const idsOwn = new Set(list.map(p => p.id));
      const merged = [...list, ...vinculados.filter(v => !idsOwn.has(v.id))];
      setProperties(merged);
      setPropsLoading(false);
      setImobImoveis(imob);
      setVinculos(vinc);
      setImobLoading(false);
    };
    load();
    const unsub = onCorretorAuthChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) navigate(`${MF_BASE}/corretor/login`, { replace: true });
    });
    return () => { cancelled = true; unsub && unsub(); };
  }, [navigate]);

  const stats = useMemo(() => {
    const total = properties.length;
    const pendentes = properties.filter(p => p.status_aprovacao === 'pendente').length;
    const aprovados = properties.filter(p => p.status_aprovacao === 'aprovado').length;
    const rejeitados = properties.filter(p => p.status_aprovacao === 'rejeitado').length;
    return { total, pendentes, aprovados, rejeitados };
  }, [properties]);

  const applyFilters = useCallback((list) => {
    let out = list;
    if (filters.busca) {
      const q = filters.busca.toLowerCase();
      out = out.filter(p =>
        (p.titulo || '').toLowerCase().includes(q) ||
        (p.bairro || '').toLowerCase().includes(q) ||
        (p.cidade || '').toLowerCase().includes(q) ||
        (p.condominio || '').toLowerCase().includes(q)
      );
    }
    if (filters.tipo_negocio === 'venda') out = out.filter(p => Number(p.preco) > 0);
    if (filters.tipo_negocio === 'locacao') out = out.filter(p => Number(p.preco_aluguel) > 0);
    if (filters.cidade) out = out.filter(p => p.cidade === filters.cidade);
    if (filters.tipo) out = out.filter(p => p.tipo === filters.tipo);
    if (filters.quartos) out = out.filter(p => Number(p.quartos) >= Number(filters.quartos));
    const precoKey = filters.tipo_negocio === 'locacao' ? 'preco_aluguel' : 'preco';
    if (filters.preco_min) out = out.filter(p => Number(p[precoKey]) >= Number(filters.preco_min));
    if (filters.preco_max) out = out.filter(p => Number(p[precoKey]) <= Number(filters.preco_max));
    return out;
  }, [filters]);

  const filteredProps = useMemo(() => applyFilters(properties), [properties, applyFilters]);
  const filteredImob = useMemo(() => applyFilters(imobImoveis), [imobImoveis, applyFilters]);

  const vinculoByImovel = useMemo(() => {
    const map = {};
    vinculos.forEach(v => { map[v.imovel_id] = v; });
    return map;
  }, [vinculos]);

  const setF = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const toggleNegocio = (val) => setFilters(prev => ({
    ...prev, tipo_negocio: prev.tipo_negocio === val ? '' : val, preco_min: '', preco_max: '',
  }));
  const faixas = filters.tipo_negocio === 'locacao' ? FAIXAS_ALUGUEL : FAIXAS_VENDA;

  const goToEditor = (p) => navigate(`${MF_BASE}/corretor/imovel/editar/${p.id}`);
  const goToPublicPage = (p, e) => {
    e.stopPropagation();
    window.open(`${MF_BASE}/imoveis/${buildPropertySlug(p)}`, '_blank', 'noopener');
  };

  const handleSolicitarAcesso = async (imovelId) => {
    setRequesting(imovelId);
    try {
      const novo = await solicitarAcesso(imovelId, c.id);
      setVinculos(prev => [...prev, novo]);
    } catch (err) {
      alert(err.message || 'Erro ao solicitar acesso.');
    } finally { setRequesting(null); }
  };

  const handleLogout = async () => {
    await signOutCorretor();
    navigate(`${MF_BASE}/corretor/login`, { replace: true });
  };

  if (state.status === 'loading') return null;

  const c = state.corretor;
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Bom dia';
    if (h < 18) return 'Boa tarde';
    return 'Boa noite';
  })();
  const firstName = (c.nome || '').split(' ')[0];
  const navLabel = nav === 'meus' ? 'Meus imóveis' : nav === 'imobiliaria' ? 'Imóveis da imobiliária' : 'Visão geral';

  return (
    <ThemeProvider theme={theme}>
      <Shell>
        <Sidebar>
          <Brand>
            <img
              src={mode === 'dark' ? '/mf-real-state/logos/mf-logo-dark.png' : '/mf-real-state/logos/mf-logo-light.png'}
              alt="MF Real Estate"
            />
            <span className="tagline">corretor</span>
          </Brand>

          <NavGroup>Imóveis</NavGroup>
          <NavItem $active={nav === 'meus'} onClick={() => setNav('meus')}>Meus imóveis</NavItem>
          <NavItem $active={nav === 'imobiliaria'} onClick={() => setNav('imobiliaria')}>Imóveis da MF</NavItem>

          <NavGroup>Conta</NavGroup>
          <NavItem onClick={handleLogout}>Sair</NavItem>
        </Sidebar>

        <Main>
          <Topbar>
            <Crumb>Corretor · <span>{navLabel}</span></Crumb>
            <TopActions>
              <ThemeToggle onClick={toggleMode} title={mode === 'dark' ? 'Modo claro' : 'Modo escuro'}>
                {mode === 'dark' ? '☀' : '☾'}
              </ThemeToggle>
              {nav === 'meus' && (
                <TopBtn onClick={() => navigate(`${MF_BASE}/corretor/imovel/novo`)}>+ Novo imóvel</TopBtn>
              )}
            </TopActions>
          </Topbar>

          <Content>
            {nav === 'meus' && (
              <>
                <PageHead>
                  <div>
                    <h1>{greeting}, {firstName}.</h1>
                    <p style={{ fontSize: 13, color: theme.muted, margin: '4px 0 0' }}>
                      CRECI {c.creci}{c.creci_uf && `-${c.creci_uf}`} · Comissão {Number(c.comissao_split).toFixed(0)}%
                    </p>
                  </div>
                </PageHead>

                <Stats>
                  <StatCard>
                    <div className="label">Total</div>
                    <div className="value">{stats.total}</div>
                    <div className="meta">Vinculados a você</div>
                  </StatCard>
                  <StatCard>
                    <div className="label">Aprovados</div>
                    <div className="value">{stats.aprovados}</div>
                    <div className="meta">Publicados</div>
                  </StatCard>
                  <StatCard>
                    <div className="label">Pendentes</div>
                    <div className="value">{stats.pendentes}</div>
                    <div className="meta">Aguardando MF</div>
                  </StatCard>
                  <StatCard>
                    <div className="label">Rejeitados</div>
                    <div className="value">{stats.rejeitados}</div>
                    <div className="meta">Ajustar</div>
                  </StatCard>
                </Stats>

                {properties.length > 0 && (
                  <FBar>
                    <FSearch placeholder="Buscar por nome, endereço..." value={filters.busca} onChange={e => setF('busca', e.target.value)} />
                    <FToggleGroup>
                      <FToggle $active={filters.tipo_negocio === 'venda'} onClick={() => toggleNegocio('venda')}>Venda</FToggle>
                      <FToggle $active={filters.tipo_negocio === 'locacao'} onClick={() => toggleNegocio('locacao')}>Locação</FToggle>
                    </FToggleGroup>
                    <FSelect value={filters.cidade} onChange={e => setF('cidade', e.target.value)}>
                      <option value="">Cidade</option>
                      {filterOptions.cidades?.map(c => <option key={c} value={c}>{c}</option>)}
                    </FSelect>
                    <FSelect value={filters.tipo} onChange={e => setF('tipo', e.target.value)}>
                      <option value="">Tipo</option>
                      {filterOptions.tipos?.map(t => <option key={t} value={t}>{t}</option>)}
                    </FSelect>
                    <FSelect value={filters.quartos} onChange={e => setF('quartos', e.target.value)}>
                      {QUARTOS_OPTS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                    </FSelect>
                    <FSelect value={filters.preco_min} onChange={e => setF('preco_min', e.target.value)}>
                      <option value="">Valor min</option>
                      {faixas.filter(v => !filters.preco_max || v < Number(filters.preco_max)).map(v => <option key={v} value={v}>{formatPrice(v)}</option>)}
                    </FSelect>
                    <FSelect value={filters.preco_max} onChange={e => setF('preco_max', e.target.value)}>
                      <option value="">Valor max</option>
                      {faixas.filter(v => !filters.preco_min || v > Number(filters.preco_min)).map(v => <option key={v} value={v}>{formatPrice(v)}</option>)}
                    </FSelect>
                    <FCount>{filteredProps.length} {filteredProps.length === 1 ? 'imóvel' : 'imóveis'}</FCount>
                  </FBar>
                )}

                {propsLoading ? (
                  <Empty>Carregando…</Empty>
                ) : filteredProps.length === 0 ? (
                  <Empty>
                    Nenhum imóvel cadastrado. Cadastre um novo ou solicite acesso aos imóveis da MF.
                  </Empty>
                ) : (
                  <Table>
                    {filteredProps.map(p => {
                      const cover = getCover(p.imagens);
                      const preco = p.preco || p.preco_aluguel;
                      return (
                        <Row key={p.id} $clickable={!p._vinculoMF} onClick={() => !p._vinculoMF && goToEditor(p)}>
                          <Thumb $src={cover} />
                          <CellInfo>
                            <div className="title">{p.titulo || 'Sem título'}</div>
                            <div className="loc">{[p.bairro, p.cidade].filter(Boolean).join(' · ') || '—'}</div>
                            <div className="price">{preco ? formatPrice(preco) : 'Sob consulta'}</div>
                          </CellInfo>
                          <CellActions className="actions">
                            {p._vinculoMF && <Badge $s="aprovado">Imóvel MF</Badge>}
                            <Badge $s={p.status_aprovacao}>
                              {p.status_aprovacao === 'aprovado' ? 'Aprovado' :
                               p.status_aprovacao === 'rejeitado' ? 'Rejeitado' : 'Pendente'}
                            </Badge>
                            {p.status_aprovacao === 'aprovado' && (
                              <SmallBtn type="button" onClick={(e) => goToPublicPage(p, e)}>
                                Ver no site
                              </SmallBtn>
                            )}
                          </CellActions>
                          {p.status_aprovacao === 'rejeitado' && p.motivo_rejeicao && (
                            <RejectMsg><strong>Motivo:</strong> {p.motivo_rejeicao}</RejectMsg>
                          )}
                        </Row>
                      );
                    })}
                  </Table>
                )}
              </>
            )}

            {nav === 'imobiliaria' && (
              <>
                <PageHead>
                  <div>
                    <h1>Imóveis da MF Real Estate</h1>
                    <p style={{ fontSize: 13, color: theme.muted, margin: '4px 0 0' }}>
                      Solicite acesso para vender imóveis da carteira da imobiliária.
                    </p>
                  </div>
                </PageHead>

                {imobImoveis.length > 0 && (
                  <FBar>
                    <FSearch placeholder="Buscar por nome, endereço..." value={filters.busca} onChange={e => setF('busca', e.target.value)} />
                    <FToggleGroup>
                      <FToggle $active={filters.tipo_negocio === 'venda'} onClick={() => toggleNegocio('venda')}>Venda</FToggle>
                      <FToggle $active={filters.tipo_negocio === 'locacao'} onClick={() => toggleNegocio('locacao')}>Locação</FToggle>
                    </FToggleGroup>
                    <FSelect value={filters.cidade} onChange={e => setF('cidade', e.target.value)}>
                      <option value="">Cidade</option>
                      {filterOptions.cidades?.map(c => <option key={c} value={c}>{c}</option>)}
                    </FSelect>
                    <FSelect value={filters.tipo} onChange={e => setF('tipo', e.target.value)}>
                      <option value="">Tipo</option>
                      {filterOptions.tipos?.map(t => <option key={t} value={t}>{t}</option>)}
                    </FSelect>
                    <FSelect value={filters.quartos} onChange={e => setF('quartos', e.target.value)}>
                      {QUARTOS_OPTS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                    </FSelect>
                    <FSelect value={filters.preco_min} onChange={e => setF('preco_min', e.target.value)}>
                      <option value="">Valor min</option>
                      {faixas.filter(v => !filters.preco_max || v < Number(filters.preco_max)).map(v => <option key={v} value={v}>{formatPrice(v)}</option>)}
                    </FSelect>
                    <FSelect value={filters.preco_max} onChange={e => setF('preco_max', e.target.value)}>
                      <option value="">Valor max</option>
                      {faixas.filter(v => !filters.preco_min || v > Number(filters.preco_min)).map(v => <option key={v} value={v}>{formatPrice(v)}</option>)}
                    </FSelect>
                    <FCount>{filteredImob.length} {filteredImob.length === 1 ? 'imóvel' : 'imóveis'}</FCount>
                  </FBar>
                )}

                {imobLoading ? (
                  <Empty>Carregando…</Empty>
                ) : filteredImob.length === 0 ? (
                  <Empty>Nenhum imóvel disponível no momento.</Empty>
                ) : (
                  <Table>
                    {filteredImob.map(p => {
                      const cover = getCover(p.imagens);
                      const preco = p.preco || p.preco_aluguel;
                      const vinculo = vinculoByImovel[p.id];
                      return (
                        <Row key={p.id}>
                          <Thumb $src={cover} />
                          <CellInfo>
                            <div className="title">{p.titulo || 'Sem título'}</div>
                            <div className="loc">{[p.condominio, p.bairro, p.cidade].filter(Boolean).join(' · ') || '—'}</div>
                            <div className="price">{preco ? formatPrice(preco) : 'Sob consulta'}</div>
                          </CellInfo>
                          <CellActions className="actions">
                            {!vinculo && (
                              <SmallBtn
                                $gold
                                onClick={() => handleSolicitarAcesso(p.id)}
                                disabled={requesting === p.id}
                              >
                                {requesting === p.id ? 'Solicitando…' : 'Solicitar acesso'}
                              </SmallBtn>
                            )}
                            {vinculo?.status === 'pendente' && <Badge $s="pendente">Aguardando</Badge>}
                            {vinculo?.status === 'aprovado' && <Badge $s="aprovado">Aprovado</Badge>}
                            {vinculo?.status === 'rejeitado' && <Badge $s="rejeitado">Rejeitado</Badge>}
                          </CellActions>
                          {vinculo?.status === 'rejeitado' && vinculo.motivo_rejeicao && (
                            <RejectMsg><strong>Motivo:</strong> {vinculo.motivo_rejeicao}</RejectMsg>
                          )}
                        </Row>
                      );
                    })}
                  </Table>
                )}
              </>
            )}
          </Content>
        </Main>
      </Shell>
    </ThemeProvider>
  );
}
